/**
 * ActionstepSavePanel
 *
 * A reusable "Save to Actionstep" button + dialog used across all VALS tools.
 *
 * Props:
 *  - noteText: string          — the formatted file note content to save
 *  - formLabel: string         — human-readable label for what's being saved (e.g. "Intake Form")
 *  - accentColor: string       — brand colour for the button (defaults to VALS red)
 *  - disabled: boolean         — disable when form has no data worth saving
 *
 * Credentials are read from env vars (VITE_ACTIONSTEP_*) if present,
 * with manual entry fallback for when they're not yet configured.
 */
import { hasSomethingToSave } from "@/lib/actionstepSave";
import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  BriefcaseBusiness,
  CheckCircle2,
  DatabaseZap,
  Loader2,
  LogIn,
  RefreshCw,
  Search,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { DEFAULT_ACTIONSTEP_ENVIRONMENT } from "@/lib/aidPlannerAdapters/actionstepClient";
import { useActionstepSession } from "@/hooks/useActionstepSession";

// Read credentials from Vite env vars (set VITE_ACTIONSTEP_* in .env.local)
const ENV_CLIENT_ID = import.meta.env.VITE_ACTIONSTEP_CLIENT_ID || "";
const ENV_CLIENT_SECRET = import.meta.env.VITE_ACTIONSTEP_CLIENT_SECRET || "";
const ENV_REDIRECT_URI = import.meta.env.VITE_ACTIONSTEP_REDIRECT_URI || "";
// Falls back to DEFAULT_ACTIONSTEP_ENVIRONMENT ("staging") rather than a hardcoded
// "production". This panel connects to a live practice management system holding real
// client matters. If the environment is unset, defaulting to production means a test
// connection lands on real data - the safe default is the one that cannot do harm.
const ENV_ENVIRONMENT = import.meta.env.VITE_ACTIONSTEP_ENVIRONMENT || DEFAULT_ACTIONSTEP_ENVIRONMENT;

const VALS_RED = "#8C1D18";

export default function ActionstepSavePanel({
  noteText = "",
  formLabel = "Form",
  accentColor = VALS_RED,
  disabled = false,
  documents = [],
  // Called at save time rather than on every render, so a page can generate a document -
  // a PDF of the form being saved - without rebuilding it on each keystroke.
  getDocuments = null,
  // Some tools produce a document and nothing else. A certification sheet is the artefact
  // itself, so a note summarising it adds a second, lesser copy of the same thing to the
  // matter chronology.
  saveNote = true,
  // Default folder for documents that do not name one. Firm-specific: see
  // src/lib/actionstepFolders.js. Empty files at the matter root.
  documentFolder = "",
}) {
  const {
    isConnected,
    isSimulator,
    connect,
    disconnect,
    searchMatters,
    saveFileNote,
    isExchanging,
    isSearching,
    isSaving,
    exchangeError,
    searchError,
    saveError,
    uploadMatterDocument,
    isUploading,
    uploadError,
  } = useActionstepSession();

  // Dialog visibility
  const [showDialog, setShowDialog] = useState(false);
  const [folderWarning, setFolderWarning] = useState("");
  const [savedSummary, setSavedSummary] = useState("Saved");

  // Step tracking: "connect" | "search" | "save"
  const [step, setStep] = useState("search");

  // OAuth manual credential entry (only shown when env vars not set)
  const [manualClientId, setManualClientId] = useState(ENV_CLIENT_ID);
  const [manualClientSecret, setManualClientSecret] = useState(ENV_CLIENT_SECRET);
  // The OAuth popup lands on the dedicated callback page, not on whichever tool started
  // the sign-in. One fixed URI to register in Actionstep, and it works from every tool.
  const [manualRedirectUri, setManualRedirectUri] = useState(
    ENV_REDIRECT_URI || (typeof window !== "undefined" ? `${window.location.origin}/actionstep-auth.html` : "")
  );
  const [manualEnvironment, setManualEnvironment] = useState(ENV_ENVIRONMENT);

  // Matter search
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedMatter, setSelectedMatter] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  // Save confirmation
  const [savedMatter, setSavedMatter] = useState(null);

  const searchInputRef = useRef(null);

  const hasEnvCreds = !!(ENV_CLIENT_ID && ENV_CLIENT_SECRET && ENV_REDIRECT_URI);

  // When dialog opens, determine starting step
  useEffect(() => {
    if (showDialog) {
      setStep(isConnected ? "search" : "connect");
      setSearchQuery("");
      setSearchResults([]);
      setSelectedMatter(null);
      setHasSearched(false);
      setSavedMatter(null);
    }
  }, [showDialog, isConnected]);

  // Focus search input when we reach the search step
  useEffect(() => {
    if (step === "search" && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [step]);

  const handleOpenDialog = () => {
    if (!hasSomethingToSave({ saveNote, noteText, getDocuments, documents })) {
      toast.error("Please fill in some form data before saving to Actionstep.");
      return;
    }
    setShowDialog(true);
  };

  const handleConnect = () => {
    connect({
      clientId: manualClientId,
      clientSecret: manualClientSecret,
      redirectUri: manualRedirectUri,
      environment: manualEnvironment,
    });
  };

  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) return;
    try {
      const results = await searchMatters(searchQuery.trim());
      setSearchResults(results);
      setHasSearched(true);
      setSelectedMatter(null);
    } catch {
      setSearchResults([]);
      setHasSearched(true);
    }
  }, [searchMatters, searchQuery]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  const handleSelectMatter = (matter) => {
    setSelectedMatter(matter);
  };

  const handleSave = async () => {
    if (!selectedMatter) return;
    const text = `${formLabel.toUpperCase()} — VALS PORTAL\n${"=".repeat(40)}\n\n${noteText}\n\n---\nSaved via CLS Portal on ${new Date().toLocaleString("en-AU")}`;
    try {
      if (saveNote) await saveFileNote(selectedMatter.externalId, text);
      // An entry is either a File, or { file, folder } when it belongs somewhere specific.
      // The certification sheet and the brief of evidence are saved together and file
      // separately, so one folder for the whole batch is not enough.
      const misfiled = [];
      const uploadedNames = [];
      const files = getDocuments ? await getDocuments() : documents;
      for (const entry of (files || []).filter(Boolean)) {
        const file = entry?.file ?? entry;
        const folder = entry?.folder ?? documentFolder;
        if (!file) continue;
        const outcome = await uploadMatterDocument(selectedMatter.externalId, file, folder);
        uploadedNames.push(file.name);
        // A document filed somewhere other than where it was meant to go is worse than a
        // visible failure: nobody notices until they go looking for it. Say so plainly,
        // and name the folders the matter does have so the cause is obvious.
        if (folder && outcome?.folder && outcome.folder.reason !== "ok") {
          const available = outcome.folder.available?.length
            ? ` Folders on this matter: ${outcome.folder.available.join(", ")}.`
            : "";
          misfiled.push(`${file.name} was filed at the matter root, not "${folder}".${available}`);
        }
      }
      const noteWritten = saveNote;
      const documentCount = uploadedNames.length;
      setSavedSummary(
        [
          noteWritten ? "File note added" : "",
          documentCount === 1 ? "Document saved" : documentCount > 1 ? `${documentCount} documents saved` : "",
        ].filter(Boolean).join(" and ") || "Saved",
      );
      setSavedMatter(selectedMatter);
      setStep("saved");
      if (misfiled.length) {
        setFolderWarning(misfiled.join(" "));
        toast.warning("Saved, but not in the expected folder.");
      } else {
        setFolderWarning("");
      }
      toast.success(
        `${saveNote ? "Saved" : "Document saved"} to Actionstep matter: ${selectedMatter.client.fullName} (${selectedMatter.client.fileNumber})`
      );
    } catch {
      // error shown via saveError from hook
    }
  };

  const accent = accentColor || VALS_RED;
  const hoverAccent = accent + "dd";

  return (
    <>
      <Button
        type="button"
        onClick={handleOpenDialog}
        disabled={disabled}
        variant="outline"
        className="rounded-xl px-4 py-2.5 text-xs font-bold uppercase tracking-wide border-slate-200 text-slate-700 hover:bg-slate-50 flex items-center gap-1.5 print:hidden"
      >
        <BriefcaseBusiness className="w-4 h-4" style={{ color: accent }} />
        Save to Actionstep{isSimulator ? " Simulator" : ""}
      </Button>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <BriefcaseBusiness className="w-5 h-5" style={{ color: accent }} />
              Save to Actionstep{isSimulator ? " Simulator" : ""}
            </DialogTitle>
            <DialogDescription>
              {step === "connect"
                ? "Sign in to Actionstep to link this form to a matter."
                : step === "search"
                ? `Search for the ${isSimulator ? "fictional simulator" : "Actionstep"} matter to save this ${formLabel} to.`
                : `Successfully saved to Actionstep${isSimulator ? " Simulator" : ""}.`}
            </DialogDescription>
          </DialogHeader>

          {/* ── CONNECT STEP ── */}
          {step === "connect" && (
            <div className="space-y-4 py-2">
              {isExchanging ? (
                <div className="flex items-center justify-center gap-3 py-6 text-slate-500">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span className="text-sm">Exchanging credentials with Actionstep…</span>
                </div>
              ) : (
                <>
                  {!hasEnvCreds && (
                    <div className="space-y-3">
                      <p className="text-xs text-slate-500 leading-relaxed">
                        Enter your Actionstep API credentials below, or add them to{" "}
                        <code className="bg-slate-100 px-1 rounded text-[11px]">.env.local</code> as{" "}
                        <code className="bg-slate-100 px-1 rounded text-[11px]">VITE_ACTIONSTEP_CLIENT_ID</code> etc.
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-[10px] font-bold uppercase text-slate-500 block mb-1">Environment</label>
                          <select
                            value={manualEnvironment}
                            onChange={(e) => setManualEnvironment(e.target.value)}
                            className="w-full border border-slate-200 rounded-lg px-2 h-9 text-xs bg-white focus:outline-none"
                          >
                            <option value="production">Production</option>
                            <option value="staging">Staging</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-[10px] font-bold uppercase text-slate-500 block mb-1">Client ID</label>
                          <Input
                            value={manualClientId}
                            onChange={(e) => setManualClientId(e.target.value)}
                            placeholder="your-client-id"
                            className="h-9 text-xs"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-[10px] font-bold uppercase text-slate-500 block mb-1">Client Secret</label>
                        <Input
                          type="password"
                          value={manualClientSecret}
                          onChange={(e) => setManualClientSecret(e.target.value)}
                          placeholder="your-client-secret"
                          className="h-9 text-xs"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-bold uppercase text-slate-500 block mb-1">Redirect URI</label>
                        <Input
                          value={manualRedirectUri}
                          onChange={(e) => setManualRedirectUri(e.target.value)}
                          placeholder="https://portal.completelawsupport.com/actionstep-auth.html"
                          className="h-9 text-xs"
                        />
                      </div>
                    </div>
                  )}

                  {exchangeError && (
                    <p className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2 border border-red-100">
                      {exchangeError}
                    </p>
                  )}

                  <div className="flex flex-col gap-2 pt-1">
                    <Button
                      onClick={handleConnect}
                      disabled={!manualClientId.trim() || !manualRedirectUri.trim()}
                      className="w-full rounded-xl text-white font-bold text-xs uppercase tracking-wide"
                      style={{ backgroundColor: accent }}
                    >
                      <LogIn className="w-4 h-4 mr-2" />
                      Sign in to Actionstep
                    </Button>
                    <p className="text-[10px] text-slate-400 text-center">
                      You'll be redirected to Actionstep to authorise access. You only need to do this once per session.
                    </p>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── SEARCH STEP ── */}
          {step === "search" && (
            <div className="space-y-4 py-2">
              {/* Connected badge */}
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-2.5 py-1">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Connected to Actionstep
                </span>
                <button
                  onClick={disconnect}
                  className="text-[10px] text-slate-400 hover:text-slate-600 flex items-center gap-1"
                >
                  <X className="w-3 h-3" /> Disconnect
                </button>
              </div>

              {/* Search input */}
              <div className="flex gap-2">
                <Input
                  ref={searchInputRef}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Search by client name or matter number…"
                  className="flex-1 h-10 text-sm rounded-xl"
                />
                <Button
                  onClick={handleSearch}
                  disabled={isSearching || !searchQuery.trim()}
                  className="rounded-xl text-white font-bold text-xs px-4"
                  style={{ backgroundColor: accent }}
                >
                  {isSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
              </div>

              {searchError && (
                <p className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2 border border-red-100">
                  {searchError}
                </p>
              )}

              {/* Results */}
              {hasSearched && (
                <div className="space-y-1.5 max-h-52 overflow-y-auto pr-0.5">
                  {searchResults.length === 0 ? (
                    <p className="text-xs text-slate-400 text-center py-4">
                      No matters found for &quot;{searchQuery}&quot;
                    </p>
                  ) : (
                    searchResults.map((matter) => {
                      const isSelected = selectedMatter?.externalId === matter.externalId;
                      return (
                        <button
                          key={matter.externalId}
                          onClick={() => handleSelectMatter(matter)}
                          className={`w-full text-left p-3 rounded-xl border text-xs transition-all flex items-start justify-between gap-2 ${
                            isSelected
                              ? "border-emerald-300 bg-emerald-50 text-emerald-900"
                              : "border-slate-100 bg-slate-50 hover:border-slate-200 hover:bg-white text-slate-700"
                          }`}
                        >
                          <div className="min-w-0">
                            <p className="font-bold truncate">{matter.client.fullName}</p>
                            <p className="text-slate-400 font-mono text-[10px] mt-0.5">{matter.client.fileNumber}</p>
                            {matter.matter?.summary && (
                              <p className="text-slate-500 text-[10px] mt-1 line-clamp-1">{matter.matter.summary}</p>
                            )}
                          </div>
                          {isSelected && <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />}
                        </button>
                      );
                    })
                  )}
                </div>
              )}

              {saveError && (
                <p className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2 border border-red-100">
                  {saveError}
                </p>
              )}
              {folderWarning && (
                <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2 border border-amber-100">{folderWarning}</p>
              )}
              {uploadError && (
                <p className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2 border border-red-100">{uploadError}</p>
              )}
            </div>
          )}

          {/* ── SAVED STEP ── */}
          {step === "saved" && savedMatter && (
            <div className="py-6 flex flex-col items-center gap-3 text-center">
              <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <div>
                <p className="font-bold text-slate-800 text-sm">Saved successfully!</p>
                {/* Says what actually happened. This read "File note added" regardless,
                    which is wrong for the tools that save a document and no note - and
                    those are now most of them. */}
                <p className="text-xs text-slate-500 mt-1">
                  {savedSummary} to{" "}
                  <strong>{savedMatter.client.fullName}</strong>{" "}
                  ({savedMatter.client.fileNumber})
                </p>
              </div>

              {/* Shown here, not only on the search step: by this point that step has been
                  replaced, so a warning rendered there could never be seen - which is why
                  a document filed at the matter root looked like an unqualified success. */}
              {folderWarning && (
                <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2 border border-amber-100 text-left">
                  {folderWarning}
                </p>
              )}
            </div>
          )}

          <DialogFooter className="flex gap-2 justify-between items-center">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setShowDialog(false)}
              className="rounded-xl text-slate-500 text-xs"
            >
              {step === "saved" ? "Close" : "Cancel"}
            </Button>

            {step === "search" && selectedMatter && (
              <Button
                onClick={handleSave}
                disabled={isSaving || isUploading}
                className="rounded-xl text-white font-bold text-xs uppercase tracking-wide"
                style={{ backgroundColor: accent }}
              >
                {isSaving || isUploading ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" /> {isUploading ? "Uploading document…" : "Saving…"}</>
                ) : (
                  <><DatabaseZap className="w-4 h-4 mr-2" /> Save to Actionstep</>
                )}
              </Button>
            )}

            {step === "search" && !selectedMatter && hasSearched && searchResults.length > 0 && (
              <p className="text-[10px] text-slate-400">Select a matter above then click Save</p>
            )}

            {step === "saved" && (
              <Button
                onClick={() => { setStep("search"); setSelectedMatter(null); setSavedMatter(null); setHasSearched(false); setSearchQuery(""); setSearchResults([]); }}
                variant="outline"
                className="rounded-xl text-xs font-bold"
              >
                <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Save to Another Matter
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
